<!-- Profile → Settings' PRIVACY (2026-10-20, Seán's spec): who can view the profile and whether
     others can contact its owner, saved as publicMetadata.privacy (subscriptionLists.js;
     the fullstack's shared/users/privacy.js holds the rule). Not public: only people who took
     part in one of the same VIZBI events can view it. Contact goes through VIZBI (an email relay,
     not built yet): the address is never shown. The "?" says what the current settings mean. -->
<template>
  <AppModalProfileSettingsSwitches
    title="Privacy"
    :rows
    :is-saving="isSaving"
    @toggle="handleToggle">
    <template #help>
      <AppHelpPopup
        id="privacyHelp"
        class="privacyHelp"
        label="About your privacy settings"
        is-in-dialog
        bounds-selector=".content">
        <span v-for="note in notes" :key="note" class="helpParagraph">{{ note }}</span>
      </AppHelpPopup>
    </template>
  </AppModalProfileSettingsSwitches>
</template>

<script setup>
import {computed, ref, watchEffect} from "vue";
import AppHelpPopup from "@/components/App/HelpPopup.vue";
import AppModalProfileSettingsSwitches from "@/components/App/Modal/Profile/SettingsSwitches";
import {useSaveIsSubscribed} from "@/composables/useSaveIsSubscribed";
import {getPrivacy} from "@/composables/subscriptionLists";
import useUserSafe from "@/composables/useUserSafe";

// sentence case for the switches here (Seán, 2026-10-20; FORMS.md § 18 had title case)
const LABELS = {
  makeMyProfilePublic: "Make my profile public",
  letOthersContactMe: "Let others contact me",
};
const NOTES = {
  isPublic: "Your profile is public: anyone can view it.",
  isRestricted: "Only people who took part in one of your VIZBI events can view your profile.",
  canContact: "People who can view your profile can contact you through VIZBI.",
  cannotContact: "No one can contact you through your profile.",
  always: "When VIZBI forwards a message to you, your email address is never shared.",
};

const {user} = useUserSafe();
const {isSaving, save} = useSaveIsSubscribed();
const privacy = ref(getPrivacy(null));
watchEffect(() => {
  if (!isSaving.value) privacy.value = getPrivacy(user.value?.publicMetadata);
});

const rows = computed(() => Object.entries(LABELS).map(([key, label]) => {
  return {key, label, value: privacy.value[key]};
}));
const notes = computed(() => {
  const {makeMyProfilePublic, letOthersContactMe} = privacy.value;
  const seen = makeMyProfilePublic ? NOTES.isPublic : NOTES.isRestricted;
  const contact = letOthersContactMe ? NOTES.canContact : NOTES.cannotContact;
  return [seen, contact, NOTES.always];
});

// on at once; back if the save fails
async function handleToggle(key, isOn) {
  if (isSaving.value) return;
  const before = privacy.value;
  privacy.value = {...before, [key]: isOn};
  if (!(await save(user.value, {privacy: {[key]: isOn}}))) privacy.value = before;
}
</script>

<style scoped>
/* the design's "?": a filled grey circle with a white mark (HelpPopup's dialog look is a green
   outline; its bubble, sized for a dialog, stays), a little larger than the heading's text */
.privacyHelp {
  --help-color: #8a8a8a;
  font-size: 15px;
}
.privacyHelp :deep(button.helpButton) {
  border-color: var(--help-color);
  background: var(--help-color);
  color: #fff;
}
</style>
